import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  getDoc,
  runTransaction,
} from 'firebase/firestore';
import { Product,  } from '@bookstore/shared';
import { Review, COLLECTIONS } from '@bookstore/shared';
import { getFirebaseFirestore } from '@bookstore/shared/browser';

class ReviewService {

  private collectionName = COLLECTIONS.REVIEWS;
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Get approved reviews for a book
   */
  async getProductReviews(productId: string): Promise<Review[]> {
    try {
      const reviewsRef = collection(this.db, this.collectionName);
      const q = query(
        reviewsRef,
        where('productId', '==', productId),
        where('isApproved', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Review[];
    } catch (error) {
      console.error('Error fetching product reviews:', error);
      let message = 'Failed to fetch reviews.';
      if (error instanceof Error) {
        message += ` ${error.message}`;
      }
      throw new Error(message);
    }
  }


  // Get user's review for a product
  async getUserReview(productId: string, userId: string): Promise<Review | null> {
    try {
      const reviewsRef = collection(this.db, this.collectionName);
      const q = query(
        reviewsRef,
        where('productId', '==', productId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as Review;
    } catch (error) {
      console.error('Error getting user review:', error);
      throw error;
    }
  }


  private async recalculateProductRating(
    productId: string,
    change: { oldRating?: number; newRating?: number; deleted?: boolean }
  ): Promise<void> {
    try {
      const productRef = doc(this.db, COLLECTIONS.PRODUCTS, productId);
      
      await runTransaction(this.db, async (tx) => {
        const productSnap = await tx.get(productRef);
        if (!productSnap.exists()) throw new Error('Product not found');
        const productData = productSnap.data() as Product;

        let sumOfRatings = productData.sumOfRatings || 0;
        let reviewCount = productData.reviewCount || 0;

        if (change.deleted) {
          sumOfRatings -= change.oldRating || 0;
          reviewCount = Math.max(reviewCount - 1, 0);
        } else if (change.oldRating !== undefined && change.newRating !== undefined) {
          sumOfRatings += (change.newRating - change.oldRating);
        } else if (change.newRating !== undefined && change.oldRating === undefined) {
          sumOfRatings += change.newRating;
          reviewCount += 1;
        }

        const newRating = reviewCount > 0 ? sumOfRatings / reviewCount : 0;

        tx.update(productRef, {
          sumOfRatings,
          reviewCount,
          rating: newRating,
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error updating product rating incrementally:', error);
      throw new Error('Failed to update product rating');
    }
  }






  /**
   * Add a new review (user)
   */
  async addReview(review: Omit<Review, 'id' | 'createdAt' | 'updatedAt' | 'isApproved'>): Promise<string> {
    try {
      const reviewsRef = collection(this.db, this.collectionName);
      //  check if user has already submitted a review for this book
      const existing = await this.getUserReview(review.productId, review.userId);
      if (existing) {
        throw new Error('User has already submitted a review for this product');
      }

      const newReview = {
        ...review,
        isApproved: true, // TODO Admin will approve later, for now lets keep it approved by default. WHY ? Because we we will provide this feature later
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const docRef = await addDoc(reviewsRef, newReview);
      await this.recalculateProductRating(review.productId, { newRating: review.rating });

      return docRef.id;
    } catch (error) {
      console.error('Error adding review:', error);
      throw error;
    }
  }

  /**
   * Edit a user’s own review
   */
  async editReview(reviewId: string, updatedData: Partial<Review>): Promise<void> {
    try {
      const reviewRef = doc(this.db, this.collectionName, reviewId);
      const oldSnap = await getDoc(reviewRef);
      if (!oldSnap.exists()) throw new Error('Review not found');
      const oldReview = oldSnap.data() as Review;
      await updateDoc(reviewRef, {
        ...updatedData,
        isApproved: true, // TODO Needs reapproval after edit WHY ? Because we will provide this feature later
        updatedAt: serverTimestamp(),
      });
      if (updatedData.rating !== undefined && updatedData.rating !== oldReview.rating) {
        await this.recalculateProductRating(oldReview.productId, {
          oldRating: oldReview.rating,
          newRating: updatedData.rating,
        });
      }
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  }

  /**
   * Delete a user’s own review
   */
  async deleteReview(reviewId: string): Promise<void> {
    try {
      const reviewRef = doc(this.db, this.collectionName, reviewId);



      const reviewDoc = await getDoc(reviewRef);
      if (!reviewDoc.exists()) {
        throw new Error('Review not found');
      }

      const reviewData = reviewDoc.data() as Review;
      const productId = reviewData.productId;

      await deleteDoc(reviewRef);
      await this.recalculateProductRating(productId, {
        oldRating: reviewData.rating,
        deleted: true,
      });
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  /**
   * Get single review by ID (optional, for edit view)
   */
  async getReviewById(reviewId: string): Promise<Review | null> {
    try {
      const reviewRef = doc(this.db, this.collectionName, reviewId);
      const docSnap = await getDoc(reviewRef);
      if (!docSnap.exists()) return null;
      return { id: docSnap.id, ...docSnap.data() } as Review;
    } catch (error) {
      console.error('Error fetching review by ID:', error);
      throw error;
    }
  }
}

export const reviewService = new ReviewService();
